import {
  GridColDef,
  GridFilterModel,
  GridLogicOperator,
} from '@mui/x-data-grid-pro';

const convertQuickFilter = (
  quickFilterValues: string[],
  columns: GridColDef[],
) => {
  const columnsAllowedToFilter = columns
    .filter((column) => column.filterable !== false)
    .map((column) => column.field);

  const result = columnsAllowedToFilter.map((field, index) => ({
    [field]: {
      value: quickFilterValues.join(' '),
      op: index === 0 ? undefined : GridLogicOperator.Or,
      cop: ['ilike'],
    },
  }));

  return result;
};

const convertFilters = (model: GridFilterModel) => {
  const { items, logicOperator } = model;

  const newModel = items.map((item, index) => {
    let op: GridLogicOperator | undefined = undefined;

    if (index === 0) {
      op = undefined;
    } else if (index > 0 && !logicOperator) {
      op = GridLogicOperator.And;
    } else {
      op = logicOperator;
    }

    return {
      [item.field]: {
        value: (item.value as string) ?? '',
        cop: item.operator.split(','),
        op,
      },
    };
  });

  return newModel;
};

const convertCustomFilter = ({
  customFilter,
  modelFilter,
}: {
  customFilter: ReturnType<typeof convertFilters>;
  modelFilter: ReturnType<typeof convertFilters>;
}) => {
  /**
   * For the first item in the modelFilter, set the operator to And
   * This is to ensure that the customFilter and modelFilter are connected
   **/

  const newModelFilter = modelFilter.map((item, index) => {
    if (index === 0) {
      return {
        ...item,
        [Object.keys(item)[0]]: {
          ...item[Object.keys(item)[0]],
          op: GridLogicOperator.And,
        },
      };
    }

    return item;
  });

  if (newModelFilter.length > 0) {
    return [...customFilter, newModelFilter];
  }

  return customFilter;
};

type Props = {
  model: GridFilterModel;
  columns: GridColDef[];
  customFilter?: ReturnType<typeof convertFilters>;
};

const convertModel = ({ model, columns, customFilter }: Props) => {
  const { quickFilterValues } = model;

  /**
   * Check if quickFilterValues is present and convert it to model
   * Also check if customFilter is present and convert it to model and merge it with quickFilterValues
   */
  if (quickFilterValues && quickFilterValues.length > 0) {
    if (customFilter && customFilter.length > 0) {
      return convertCustomFilter({
        customFilter,
        modelFilter: convertQuickFilter(quickFilterValues as string[], columns),
      });
    }

    return convertQuickFilter(quickFilterValues as string[], columns);
  }

  /**
   * Check if customFilter is present and convert it to model
   */
  if (customFilter && customFilter.length > 0) {
    return convertCustomFilter({
      customFilter,
      modelFilter: convertFilters(model),
    });
  }

  return convertFilters(model);
};

export default convertModel;
